import React, {useCallback} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';

import {$invocieBillingBinaryPdf} from '@/entities/subscriptions/model';
import {Button, ButtonVariant} from '@/shared/ui/Button';
import {Icon} from '@/shared/ui/Icon';
import {Modal} from '@shared/ui/Modal';

import {ButtonWrapper, Description, DescriptionBold} from './styled';

const PDF_FILENAME = 'invoice.pdf';

function downloadPdfFromArrayBuffer(buffer: ArrayBuffer, filename: string) {
    const blob = new Blob([buffer], {type: 'application/pdf'});
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    URL.revokeObjectURL(url);
}

export const DownloadInvoice = ({show, onClose}: {show: boolean; onClose: () => void}) => {
    const invocieBillingBinaryPdf = useStore($invocieBillingBinaryPdf);
    const {t} = useTranslation();
    const handleDownload = useCallback(() => {
        if (!invocieBillingBinaryPdf) return;
        if (!(invocieBillingBinaryPdf instanceof ArrayBuffer)) {
            toast.error('Invalid invoice format');
            return;
        }
        try {
            downloadPdfFromArrayBuffer(invocieBillingBinaryPdf, PDF_FILENAME);
            onClose();
        } catch {
            toast.error('Failed to download invoice');
        }
    }, [invocieBillingBinaryPdf, t, onClose]);

    return (
        <Modal
            closable={true}
            show={show}
            onClose={onClose}
            title={t('subscriptions.downloadInvoice.title')}
            modalWidth={0}
        >
            <Description>{t('subscriptions.downloadInvoice.description')}</Description>
            <DescriptionBold>{t('subscriptions.downloadInvoice.description2')}</DescriptionBold>
            <footer>
                <ButtonWrapper>
                    <div>
                        <Button
                            variant={ButtonVariant.Secondary}
                            disabled={!invocieBillingBinaryPdf}
                            onClick={handleDownload}
                        >
                            <Icon
                                name="download2"
                                width={16}
                                height={16}
                                style={{marginRight: 8}}
                            />
                            <span>{t('subscriptions.button.downloadInvoice')}</span>
                        </Button>
                    </div>
                </ButtonWrapper>
            </footer>
        </Modal>
    );
};
